import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CampaignService } from '../../services/campaign.service';
import { Campaign } from '../../models/campaign';
import { RouterModule } from '@angular/router';
import { TaskComponent } from '../task/task.component';
import { CampaignTask } from '../../models/campaignTask';
import { parseISO, formatDistanceToNow } from 'date-fns';
import { UserService } from '../../services/user.service';
import { map, switchMap } from 'rxjs';

@Component({
  selector: 'app-notifications',
  imports: [CommonModule, FormsModule, RouterModule, TaskComponent],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css'
})
export class NotificationsComponent implements OnInit, OnChanges {

  @Input() filteredCampaigns: Campaign[] | null = null;

  // Changes when the parent has (re)loaded the due tasks into the campaigns, so the table re-reads them.
  @Input() tasksVersion: number = 0;

  filteredTasks: CampaignTask[] = [];
  sortedFilteredTasks: CampaignTask[] = [];

  campaigns: Campaign[] = [];

  agents: string[] = [];
  agentFilter: string = 'All';

  showInvoiceTasks: boolean = false;
  showCreatorFacingOnly: boolean = false;

  coordinators: string[] = [];
  coordinatorFilter: string = 'All';

  today: Date = new Date();
  fiveDaysOut: Date = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);

  constructor(private campaignService: CampaignService, private userService: UserService) { }

  ngOnInit(): void {
    // If filtered campaigns are provided as input, use them
    if (this.filteredCampaigns) {
      this.campaigns = this.filteredCampaigns;
      this.initializeFilters();
      this.setFilteredTasks();
    } else {
      // Otherwise, load all campaigns from service
      // Standalone use: lean campaign list, then the past due / due today tasks this table shows.
      this.campaignService.getCampaigns().pipe(
        switchMap(campaigns => this.campaignService.loadDueTasks().pipe(map(() => campaigns)))
      ).subscribe({
        next: (result) => {
          this.campaigns = result;
          this.initializeFilters();
          this.setFilteredTasks();
        },
        error: (err) => {
          console.log(err);
        }
      })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filteredCampaigns'] && this.filteredCampaigns) {
      this.campaigns = this.filteredCampaigns;
      this.initializeFilters();
      this.setFilteredTasks();
    } else if (changes['tasksVersion'] && this.campaigns.length > 0) {
      // The parent finished loading the due tasks into the campaigns
      this.setFilteredTasks();
    }
  }

  private initializeFilters(): void {
    const uniqueAgentIds = Array.from(new Set(
      this.campaigns.map(c => c.agentId).filter(id => !!id)
    )).sort((a, b) => this.resolveFirstName(a).localeCompare(this.resolveFirstName(b)));

    const uniqueCoordinatorIds = Array.from(new Set(
      this.campaigns.map(c => c.coordinatorId).filter(id => !!id)
    )).sort((a, b) => this.resolveFirstName(a).localeCompare(this.resolveFirstName(b)));

    this.agents = ['All', ...uniqueAgentIds];
    this.coordinators = ['All', ...uniqueCoordinatorIds];
  }

  resolveFirstName(id: string): string {
    return this.userService.resolveUserId(id).split(' ')[0] || id;
  }

  get pastDueCount(): number {
    return this.sortedFilteredTasks.filter(t => t.pastDue).length;
  }

  get pastDueCreatorFacingCount(): number {
    return this.sortedFilteredTasks.filter(t => t.pastDue && t.creatorFacing).length;
  }

  setFilteredTasks() {
    this.filteredTasks = [];

    this.campaigns.forEach(c => {
      if (c.deleted) {
        return;
      }

      if (!c.tasks) {
        console.warn(`Notifications: Campaign "${c.name}" (id: ${c.id}) has an undefined tasks array — skipping.`, c);
        return;
      }

      c.tasks.forEach(t => {
        if (!t) {
          console.warn(`Notifications: Campaign "${c.name}" (id: ${c.id}) contains a null/undefined task entry — skipping.`, c);
          return;
        }

        // Filter by paymentRelated and showInvoiceTasks
        if (this.showInvoiceTasks && !t.paymentRelated) {
          return;
        }
        if (!this.showInvoiceTasks && t.paymentRelated) {
          return;
        }

        // Filter by creatorFacing
        if (this.showCreatorFacingOnly && !t.creatorFacing) {
          return;
        }

        let dateString = 'No due date';
        let sortableDate = '1900-01-01'; // Far past date for sorting - will show as overdue

        if (t.dueDate && t.dueDate.trim() !== '') {
          try {
            dateString = formatDistanceToNow(parseISO(t.dueDate), {addSuffix: true});
            sortableDate = t.dueDate;
          } catch (error) {
            dateString = 'Invalid date';
          }
        }

        const newTask = new CampaignTask();
        newTask.id = t.id;
        newTask.campaignId = c.id;
        newTask.campaignName = c.name;
        newTask.description = t.description;
        newTask.dueDate = sortableDate;
        newTask.dueDateString = dateString;
        newTask.complete = t.complete;
        newTask.paymentRelated = t.paymentRelated;
        newTask.creatorFacing = t.creatorFacing;
        newTask.creatorName = c.creatorName;

        if (newTask.pastDue || newTask.dueToday) {
          const agentOk = this.agentFilter === 'All' || c.agentId === this.agentFilter;
          const coordinatorOk = this.coordinatorFilter === 'All' || c.coordinatorId === this.coordinatorFilter;
          if (agentOk && coordinatorOk) {
            this.filteredTasks.push(newTask);
          }
        }
      })
    });

    this.sortedFilteredTasks = this.filteredTasks.sort((t1, t2) => {
      const date1 = t1.dueDate || '1900-01-01';
      const date2 = t2.dueDate || '1900-01-01';
      return new Date(date1).getTime() - new Date(date2).getTime();
    });
  }

  onTaskChanged() {
    this.setFilteredTasks();
  }


}
